import { Component } from '@theme/component'

/**
 * A custom element that displays a sticky add to cart section.
 *
 * @typedef {Object} StickyATCRefs
 * @property {HTMLDivElement} container - The container element.
 * @property {HTMLDivElement} mobileVariantPicker - The mobile variant picker element.
 * @property {HTMLButtonElement} mobileVariantPickerToggle - The mobile variant picker toggle button.
 *
 * example usage:
 * ```html
 * <sticky-atc>
 *  <div ref="container">content</div>
 *  <div ref="mobileVariantPicker">content</div>
 *  <button ref="mobileVariantPickerToggle">Toggle mobile variant picker</button>
 * </sticky-atc>
 * ```
 *
 * @extends {Component<StickyATCRefs>}
 */
class StickyATC extends Component {
  requiredRefs = [
    'container',
    'mobileVariantPicker',
    'mobileVariantPickerToggle',
  ]

  /** @type {HTMLDivElement | null} */
  #productInformationEle = document.querySelector('.product-information')

  /** @type {IntersectionObserver | null} */
  #productInformationObserver = null

  /** @type {NodeListOf<HTMLButtonElement>} */
  #addToCartButtons = this.querySelectorAll('.sticky-atc__add-to-cart-button')

  connectedCallback() {
    super.connectedCallback()

    if (this.#productInformationEle) {
      this.#productInformationObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.refs.container.toggleAttribute('inert', entry.isIntersecting)
        })
      })
      this.#productInformationObserver.observe(this.#productInformationEle)
    }

    this.#addToCartButtons.forEach((button) => {
      button.addEventListener('click', this.#closeMobileVariantPicker)
    })

    this.refs.mobileVariantPickerToggle.addEventListener(
      'click',
      this.#toggleMobileVariantPicker
    )
    document.addEventListener('click', this.#handleClickOutside)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.#productInformationObserver) {
      this.#productInformationObserver.disconnect()
    }

    this.refs.mobileVariantPickerToggle.removeEventListener(
      'click',
      this.#toggleMobileVariantPicker
    )
    document.removeEventListener('click', this.#handleClickOutside)
  }

  #toggleMobileVariantPicker = () => {
    const isInert = this.refs.mobileVariantPicker.hasAttribute('inert')
    this.refs.mobileVariantPicker.toggleAttribute('inert', !isInert)
  }

  #closeMobileVariantPicker = () => {
    this.refs.mobileVariantPicker.setAttribute('inert', '')
  }

  /**
   * Handles the click outside event to close the mobile variant picker.
   * @param {MouseEvent} event - The click event.
   */
  #handleClickOutside = (event) => {
    if (!(event.target instanceof Node)) {
      return
    }

    if (
      this.refs.mobileVariantPickerToggle.contains(event.target) ||
      this.refs.mobileVariantPicker.hasAttribute('inert')
    ) {
      return
    }

    if (!this.refs.mobileVariantPicker.contains(event.target)) {
      this.refs.mobileVariantPicker.setAttribute('inert', '')
    }
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyATC)
}
